import { ColDef, ICellRendererParams, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle, SmallTitleWithSubSpan } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { formatDate } from "src/utils/string/stringUtil";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import {
    CreateCellClickHandler, CustomCellClickAction, createCellCssClassRules, createCalendarCellClickHandler, createGridDropDownCommitAfter,
    getSwitchButtonColumnOptions, GridCellConfig, postSortHandler, RemoveRowHandler, useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { HRM3040_R01PositionAllowDto } from "./Dto_HRM3040_R01";
import { useHRM3040_R01Store } from "./Store_HRM3040_R01";
import { positionHeaderNames_HRM3040_R01 as headerNames, HRM3040_R01GridType } from "./Util_HRM3040_R01";
import { useHRM3040_R01CellChange } from "./hooks/HRM3040_R01CellChange";

/** 직책수당 기준 컬럼 배치 저장 키이다. */
const COLUMN_STATE_KEY = 'HRM3040_R01-position-grid-column-state';
/** 클릭·키보드로 직접 편집할 컬럼이다. 날짜는 공통 달력, 스위치는 자체 조작을 사용한다. */
const EDITABLE_COLUMNS = ['jobPosition', 'allowAmt', 'remark'];
/** 신규 행에서만 편집하는 복합키 컬럼이다. */
const KEY_COLUMNS = ['startDt', 'jobPosition'];

/** 직책수당 기준 그리드의 참조와 행 추가·클릭 상태를 전달받는다. */
interface Props {
    clickable: boolean; // true이면 행 클릭 선택 처리
    setClickable: (value: boolean) => void; // +/- 직후 클릭을 한 번 무시하도록 변경
    gridRef: React.RefObject<AgGridReact<HRM3040_R01PositionAllowDto> | null>; // 편집 확정·스크롤에 사용할 그리드 참조
    defaultHRM3040_R01PositionRow: HRM3040_R01PositionAllowDto; // 새 행의 전체 기본값
    handleInsertClick: (event: React.MouseEvent, rowIndex?: number, gridType?: HRM3040_R01GridType) => void; // 행 추가 처리
}

/** 직책수당 기준의 기간별 기준을 편집하는 표이다. props의 참조·기본값·클릭 상태를 공통 Grid 구성에 연결해 반환한다. */
export const PositionTable_HRM3040_R01 = (props: Props) => {

    // 구조 분해로 이 표에 전달된 참조·기본값·행 추가 함수를 꺼낸다.
    const { clickable, setClickable, gridRef, defaultHRM3040_R01PositionRow, handleInsertClick } = props;

    // Store Hook에서 이 표의 목록·원본·선택·삭제와 배치·스크롤 변경 함수를 읽는다.
    const {
        HRM3040_R01PositionList, // 직책수당 기준 편집 목록
        setHRM3040_R01PositionList, // 조회한 직책수당 기준 목록을 저장하는 함수
        removeHRM3040_R01PositionList, // 직책수당 신규 행을 제거하는 함수
        updateHRM3040_R01PositionList, // 편집한 직책수당 행을 교체하는 함수
        selectedHRM3040_R01PositionList, // 선택한 직책수당 행의 식별값 목록
        addSelectedHRM3040_R01PositionList, // 직책수당 행 선택을 전달한 식별값으로 교체하는 함수
        removeSelectedHRM3040_R01PositionList, // 직책수당 행 선택을 해제하는 함수
        initSelectedHRM3040_R01PositionList, // 직책수당 행 선택을 초기화하는 함수
        allSelectedHRM3040_R01PositionList, // 직책수당 행을 전체 선택하는 함수
        deletedHRM3040_R01PositionList, // 삭제 표시한 직책수당 행의 식별값 목록
        addDeletedHRM3040_R01PositionList, // 직책수당 행에 삭제 표시를 추가하는 함수
        originalHRM3040_R01PositionList, // 변경 여부를 비교할 직책수당 조회 원본
        positionColumnState, // 직책수당 표의 컬럼 배치
        setPositionColumnState, // 직책수당 표의 컬럼 배치를 저장하는 함수
        positionScrollTop, // 직책수당 표의 스크롤 위치
        setPositionScrollTop, // 직책수당 표의 스크롤 위치를 저장하는 함수
    } = useHRM3040_R01Store();

    // 앱 초기 조회에서 준비한 직책코드 목록을 재사용한다.
    const { jcCdList } = useCommonCodeStore();

    // 두 표의 달력 대상이 섞이지 않도록 현재 표 안에서만 열림·위치·대상 셀 상태를 기억한다.
    const [HRM3040_R01PositionGridCalendarState, setCalendarState] = useState({
        pickerVisible: false, // true이면 공통 날짜 선택기를 표시한다.
        pickerPosition: { top: 0, left: 0 }, // 클릭한 셀을 기준으로 공통 핸들러가 계산한 표시 위치
        targetRowIndex: null as number | null, // 달력을 연 행의 화면 표시 순서
        targetField: null as string | null, // 달력을 연 날짜 필드(startDt 또는 endDt)
        targetDate: undefined as Date | undefined, // 공통 날짜 선택기에 표시할 현재 날짜
    });

    /** 공통 달력 클릭 핸들러가 전달한 일부 상태만 병합한다. 반환값은 없다. */
    const setHRM3040_R01PositionGridCalendarState = useCallback((patch: Partial<typeof HRM3040_R01PositionGridCalendarState>) => {

        // 펼치기로 이전 위치·대상을 보존하고 전달된 항목만 바꾼다.
        setCalendarState(previous => ({ ...previous, ...patch }));
    }, []);

    // 공통 행 선택 훅은 숫자 valueIndex로 비교하므로 문자열 저장값을 숫자로 바꾸어 전달한다.
    const selectedIdsForHook = useMemo(() => selectedHRM3040_R01PositionList.map(Number) as unknown as string[], [selectedHRM3040_R01PositionList]);
    const deletedIdsForHook = useMemo(() => deletedHRM3040_R01PositionList.map(Number) as unknown as string[], [deletedHRM3040_R01PositionList]);

    // 드래그 중 정렬 처리를 피하기 위한 기준 화면의 공통 참조이다. true이면 드래그 중이다.
    const isRowDraggingRef = useRef(false);

    // 기준 Table과 동일한 순서로 행 ID·CSS·표시 목록·index 렌더러·스위치 동작을 받는다.
    const { getRowId, createRowClassRules, createCellClassRules, paddedGridData, renderIndexCell, handleSwitchButtonHandler } =
        useGridSettingContent<HRM3040_R01PositionAllowDto>({
            gridRef, key: 'valueIndex', gridData: HRM3040_R01PositionList, defaultGridData: defaultHRM3040_R01PositionRow,
            selectedRow: selectedHRM3040_R01PositionList, originalGridData: originalHRM3040_R01PositionList, updateRow: updateHRM3040_R01PositionList,
        });

    // CSS 설정은 고정 컬럼 기준으로 한 번만 만든다. 저장된 키 칸은 비활성 회색이다.
    const cssCellConfig = useMemo<GridCellConfig>(() => ({
        disabledCells: [], insertedDisabledCells: [], commonDisabledCells: KEY_COLUMNS,
        pointerCells: ['endDt', 'allowAmt', 'useYn', 'remark'],
        insertedPointerCells: KEY_COLUMNS, commonPointerCells: [], alignLeftCells: ['remark'],
    }), []);

    // 공통 CSS 유틸이 신규·기존 행용 컬럼 목록을 계산한다.
    const { disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns } =
        useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

    // 기본 셀 규칙은 선택·삭제 상태가 바뀔 때 갱신하며 하단 건수는 공통 규칙으로 가운데 정렬한다.
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM3040_R01PositionAllowDto>({
            idKey: 'valueIndex', selectedList: selectedHRM3040_R01PositionList.map(Number), deletedList: deletedHRM3040_R01PositionList.map(Number),
            disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns,
        }),
    }), [createCellClassRules, selectedHRM3040_R01PositionList, deletedHRM3040_R01PositionList, disabledInsertedColumns, disabledColumns,
        pointerInsertedColumns, pointerColumns, alignLeftColumns]);

    /** 하단 고정 행·삭제 행을 제외한 실제 행이면 편집할 수 있다. true가 편집 허용이다. */
    const editable = useCallback((params: { node: { rowPinned?: string | null }; data?: HRM3040_R01PositionAllowDto }) =>
        !params.node.rowPinned && !!params.data && !deletedHRM3040_R01PositionList.includes(String(params.data.valueIndex)), [deletedHRM3040_R01PositionList]);

    // 공통 드롭다운 확정 후 다음 편집 셀로 이동하는 함수를 컬럼 목록 기준으로 기억한다.
    const handleGridDropDownCommitAfter = useMemo(
        () => createGridDropDownCommitAfter<HRM3040_R01PositionAllowDto>(EDITABLE_COLUMNS), []);

    /** 행 번호 - 버튼을 공통 신규 제거·기존 삭제 표시 처리에 전달한다. */
    const handleRemoveRow = useCallback((row: HRM3040_R01PositionAllowDto) => {
        setClickable(false);
        RemoveRowHandler(gridRef, HRM3040_R01PositionList, row, setClickable, removeHRM3040_R01PositionList, addDeletedHRM3040_R01PositionList);
    }, [gridRef, HRM3040_R01PositionList, setClickable, removeHRM3040_R01PositionList, addDeletedHRM3040_R01PositionList]);

    /**
     * 공통 달력의 선택 날짜를 현재 표시 행에 반영한다.
     * @param date 선택 날짜. 값이 없으면 기준 화면과 같이 오늘을 사용한다.
     * @param rowIndex 정렬이 반영된 화면 행 순서
     * @param field 시작일 또는 종료일 필드
     * @returns 반환값 없이 해당 행과 수정 상태를 갱신한다.
     */
    const handleGridCalendarChange = useCallback((date: Date | undefined, rowIndex: number, field: string) => {
        if (field !== 'startDt' && field !== 'endDt') return;

        // ?.는 표시 순서의 행이 없거나 그리드가 닫혔으면 해당 접근을 건너뛴다.
        const valueIndex = gridRef.current?.api.getDisplayedRowAtIndex(rowIndex)?.data?.valueIndex; // 정렬 후에도 유지되는 행 식별값
        const current = useHRM3040_R01Store.getState(); // 달력을 연 뒤 바뀐 목록·삭제 표시까지 확인할 최신 상태
        const row = current.HRM3040_R01PositionList.find(item => item.valueIndex === valueIndex); // 실제로 변경할 업무 행
        if (!row || current.deletedHRM3040_R01PositionList.includes(String(row.valueIndex))) return;
        if (field === 'startDt' && !row.inserted) return;

        // ??는 날짜가 없을 때만 오늘을 사용한다. 공통 포맷으로 YYYY-MM-DD를 저장한다.
        const newValue = formatDate(date ?? new Date()); // API에 보낼 선택 날짜
        if (row[field] === newValue) return;
        updateHRM3040_R01PositionList(row.valueIndex, { ...row, [field]: newValue, isModified: !row.inserted });
    }, [gridRef, updateHRM3040_R01PositionList]);

    /** 달력을 열 때 현재 표시 행의 날짜를 다시 읽어 공통 날짜 선택기에 전달한다. */
    const handleGridCalendarLoad = useCallback((rowIndex: number, field: string) => {
        if (field !== 'startDt' && field !== 'endDt') return;
        const row = gridRef.current?.api.getDisplayedRowAtIndex(rowIndex)?.data; // 현재 표시 순서의 업무 행
        const dateValue = row?.[field]; // 해당 행의 시작일 또는 종료일

        // 삼항 연산자: 값이 있으면 그 날짜, 없으면 오늘을 달력에 표시한다.
        setHRM3040_R01PositionGridCalendarState({ targetDate: dateValue ? new Date(dateValue) : new Date() });
    }, [gridRef, setHRM3040_R01PositionGridCalendarState]);

    // 컬럼 정의는 기준 표의 index·업무 컬럼 순서와 공통 편집기를 사용한다.
    const baseColumnDefs = useMemo<ColDef<HRM3040_R01PositionAllowDto>[]>(() => [
        // 펼치기: 공통 index 컬럼 배열을 업무 컬럼 앞에 추가한다.

        ...getDefaultRowIndexColumnConfig((params: ICellRendererParams<HRM3040_R01PositionAllowDto>) => renderIndexCell(
            params,
            inner => getRowStyleFlags(inner, { deletedList: deletedIdsForHook, selectedList: selectedIdsForHook, dataList: HRM3040_R01PositionList }),
            (rowIndex: number, event: React.MouseEvent) => {

                // Ctrl + 클릭은 기준 Table처럼 10행을 추가한다.
                const count = event.ctrlKey ? 10 : 1; // 이 클릭으로 생성할 행 수
                for (let i = 0; i < count; i++) handleInsertClick(event, rowIndex, 'position');
            },
            handleRemoveRow,
        )),
        {
            headerName: headerNames.startDt, colId: 'startDt', field: 'startDt', width: 116,
            // ?.는 데이터가 있을 때만 inserted를 읽는다. 저장된 기본키는 변경할 수 없다.
            editable: params => editable(params) && params.data?.inserted === true,

            // 날짜 클릭은 아래 공통 달력 핸들러가 처리한다. 편집 가능 여부는 클릭 전달과 키 고정에 사용한다.
            // ??는 값이 없을 때만 빈 문자열을 쓴다. 하단 N건도 그대로 보존한다.
            valueFormatter: params => String(params.value ?? ''),
        },
        {
            headerName: headerNames.endDt, colId: 'endDt', field: 'endDt', width: 116,
            // 종료일도 브라우저 기본 달력 대신 공통 GridCalendar에서 선택한다.
            editable,
            valueFormatter: params => String(params.value ?? ''),
        },
        {
            headerName: headerNames.jobPosition, colId: 'jobPosition', field: 'jobPosition', width: 90,
            ...CommonCodeColumn.options<HRM3040_R01PositionAllowDto>(jcCdList,
                params => editable(params) && params.data?.inserted === true,
                CommonCodeColumn.change(updateHRM3040_R01PositionList), handleGridDropDownCommitAfter),
        },
        {
            headerName: headerNames.allowAmt, colId: 'allowAmt', field: 'allowAmt', width: 100, editable,
            // 입력의 쉼표와 공백을 모두 제거하는 패턴이다. 숫자로 변환하지 못하면 0을 쓴다.
            valueParser: params => Number(String(params.newValue ?? '').replace(/[,\\s]/g, '')) || 0,
            valueFormatter: params => params.value == null ? '' : Number(params.value).toLocaleString('ko-KR', { maximumFractionDigits: 0 }),
        },
        {
            headerName: headerNames.useYn, colId: 'useYn', field: 'useYn', width: 56,
            ...getSwitchButtonColumnOptions<HRM3040_R01PositionAllowDto>('useYn', handleSwitchButtonHandler,
                headerNames, aggridSwitchButtonWrapperStyle, editable),
        },
        {
            headerName: headerNames.remark, colId: 'remark', field: 'remark', width: 150, editable, pasteable: true,
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 1000 },
        },
    ], [renderIndexCell, deletedIdsForHook, selectedIdsForHook, HRM3040_R01PositionList, handleInsertClick, handleRemoveRow,
        editable, handleSwitchButtonHandler, jcCdList, updateHRM3040_R01PositionList, handleGridDropDownCommitAfter]);

    // 기본 배치는 현재 컬럼 정의가 바뀔 때만 다시 생성한다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);

    // 저장된 배치가 없으면 기본 정의를 그대로 쓰고, 있으면 공통 함수 결과를 그대로 반환한다.
    const columnDefs = useMemo(() => {
        if (positionColumnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<HRM3040_R01PositionAllowDto>({ baseColumnDefs, columnState: positionColumnState, headerNames, fallbackHeaderClass: aggridHeaderClass });
    }, [baseColumnDefs, positionColumnState]);

    // 셀 변경은 전달한 행 교체 함수를 사용하는 화면 전용 훅으로 처리한다.
    const { handleCellChange } = useHRM3040_R01CellChange<HRM3040_R01PositionAllowDto>({ updateRow: updateHRM3040_R01PositionList });

    // 숫자로 변환한 식별값을 비교하되 Store에는 문자열로 저장한다.
    const handleRowClick = useRowSelectionHandler<HRM3040_R01PositionAllowDto>({
        dataList: HRM3040_R01PositionList, idKey: 'valueIndex', selectedList: selectedIdsForHook, deletedList: deletedIdsForHook,
        addSelected: addSelectedHRM3040_R01PositionList, removeSelected: removeSelectedHRM3040_R01PositionList, clickable, setClickable,
    });

    // 기준 Table의 키보드 이동 흐름을 그대로 사용한다.
    const cellEventHandlers = useDetailTableEvents({ detailList: HRM3040_R01PositionList, editableColumns: EDITABLE_COLUMNS, keyDownConfig: { enableArrowNavigation: true } });

    // 날짜는 HRM1030 교육 이력과 같은 공통 달력을 열고, 다른 필드만 셀 편집기를 연다.
    const cellClickActions = useMemo<CustomCellClickAction<HRM3040_R01PositionAllowDto>[]>(() => [
        {
            validate: (field, data) => field === 'startDt' && data.inserted && !deletedHRM3040_R01PositionList.includes(String(data.valueIndex)),
            action: createCalendarCellClickHandler<HRM3040_R01PositionAllowDto>('startDt', HRM3040_R01PositionGridCalendarState, setHRM3040_R01PositionGridCalendarState),
        },
        {
            validate: (field, data) => field === 'endDt' && !deletedHRM3040_R01PositionList.includes(String(data.valueIndex)),
            action: createCalendarCellClickHandler<HRM3040_R01PositionAllowDto>('endDt', HRM3040_R01PositionGridCalendarState, setHRM3040_R01PositionGridCalendarState),
        },
        {
            validate: (field, data) => EDITABLE_COLUMNS.includes(field) && !deletedHRM3040_R01PositionList.includes(String(data.valueIndex))
                && (!KEY_COLUMNS.includes(field) || data.inserted),
            /** 허용된 일반 업무 셀의 편집기를 연다. */
            action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
        },
    ], [deletedHRM3040_R01PositionList, HRM3040_R01PositionGridCalendarState, setHRM3040_R01PositionGridCalendarState]);
    const handleCellClick = CreateCellClickHandler(HRM3040_R01PositionList, 'valueIndex', cellClickActions); // 공통 셀 클릭 처리

    /** 기준 Table과 같은 정렬 후 표시 순서 처리를 수행한다. */
    const handlePostSortWithDrag = useCallback((params: PostSortRowsParams<HRM3040_R01PositionAllowDto>) => {
        if (isRowDraggingRef.current) return;
        postSortHandler(params, HRM3040_R01PositionList, originalHRM3040_R01PositionList, setHRM3040_R01PositionList);
    }, [HRM3040_R01PositionList, originalHRM3040_R01PositionList, setHRM3040_R01PositionList]);

    // 하단 행 자체는 세지 않고 삭제 표시 행은 포함한다.
    const pinnedBottomRow = useMemo(() => [{ startDt: HRM3040_R01PositionList.length.toLocaleString() + '건' }], [HRM3040_R01PositionList.length]);

    return (
        // 우클릭·전체 선택·컬럼 배치를 기준 화면과 같은 공통 Wrapper로 연결한다.
        <GridContextWrapper<HRM3040_R01PositionAllowDto>
            enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData}
            initSelected={initSelectedHRM3040_R01PositionList}
            defaultRowData={defaultHRM3040_R01PositionRow}
            allSelected={allSelectedHRM3040_R01PositionList}
            baseColumnDefs={columnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setPositionColumnState}
            localStorageKey={COLUMN_STATE_KEY}>

            {/* 공통 파란 막대 제목. 건수는 아래 그리드의 하단 행에 표시한다. */}
            <SmallTitleWithSubSpan title="직책수당 기준" />

            {/* 편집·선택·삭제 표시와 공통 키보드·정렬 흐름을 연결한 업무 그리드이다. */}
            <Grid_Excel<HRM3040_R01PositionAllowDto>
                ref={gridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={cellCssDefaultColDef}
                rowClassRules={createRowClassRules<HRM3040_R01PositionAllowDto>({ idKey: 'valueIndex', selectedList: selectedHRM3040_R01PositionList.map(Number), deletedList: deletedHRM3040_R01PositionList.map(Number) })}
                onRowClicked={handleRowClick}
                onCellClicked={handleCellClick}
                onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                onCellValueChanged={handleCellChange}
                pinnedBottomRowData={pinnedBottomRow}
                onPostSortRows={handlePostSortWithDrag}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setPositionColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={positionScrollTop}
                setScrollTop={setPositionScrollTop} />

            {/* 시작·종료일은 공통 GridCalendar의 디자인과 셀 기준 표시 위치를 사용한다. */}
            <GridCalendar
                rowIndex={HRM3040_R01PositionGridCalendarState.targetRowIndex ?? 0}
                targetField={HRM3040_R01PositionGridCalendarState.targetField ?? ''}
                visible={HRM3040_R01PositionGridCalendarState.pickerVisible}
                setVisible={visible => setHRM3040_R01PositionGridCalendarState({ pickerVisible: visible })}
                value={HRM3040_R01PositionGridCalendarState.targetDate}
                pickerPosition={HRM3040_R01PositionGridCalendarState.pickerPosition}
                setPickerPosition={position => setHRM3040_R01PositionGridCalendarState({ pickerPosition: position })}
                onClear={() => setHRM3040_R01PositionGridCalendarState({ targetDate: undefined })}
                onChange={handleGridCalendarChange}
                onClickLoad={handleGridCalendarLoad} />
        </GridContextWrapper>
    );
};
